/**
 * ================================================================================================
 *  ⓘ — THE EXPLANATION THAT IS THERE WHEN SOMEBODY WANTS IT, AND NOWHERE WHEN THEY DO NOT
 * ================================================================================================
 *
 * The Owner, 2026-10-28, after asking four times for the screens to be cleaned:
 *
 *     *"אני לא אעבור איתך אחד אחד. תעבור על כל המערכת — לא צריך את כל המידע הזה, ואם ממש חשוב
 *     שיהיה הסבר תוסיף סימן של i שהוא למעשה INFORMATION והם יוכלו לקרוא. אבל לא צריך את כל זה, זה
 *     ממש מיותר. תראה את האייקונט, איזה נקי זה."*
 *
 * This component is that sign. A screen says what a thing IS; if the reason behind it genuinely
 * matters, the reason goes behind this.
 *
 * ── IT IS A `<details>`, AND THAT IS THE WHOLE IMPLEMENTATION ───────────────────────────────────
 *
 * No popover, no portal, no floating layer, no state. The browser already has a disclosure element
 * that is keyboard-reachable, screen-reader-announced, printable when open and impossible to leave
 * stranded over a scrolled page. A hand-built tooltip would be a second implementation of something
 * every browser ships, and the one thing it would add is a layer that can be left open behind a
 * navigation.
 *
 * ON HOVER IT DOES NOTHING. A tooltip that appears on hover cannot be reached from a keyboard and
 * is unreadable on a touch screen, and this product is used on both. It opens on click or on Enter,
 * like everything else that opens.
 *
 * ── WHAT IT IS NOT FOR ─────────────────────────────────────────────────────────────────────────
 *
 * It is not a place to put the text that was removed from the screens. Most of that text is simply
 * gone, because it explained the product to a person who runs an accounting office. This is for the
 * few facts a reader needs in order to DECIDE and cannot get from the control itself — what a
 * closed vocabulary's values mean, or which of two near-identical things they are looking at.
 *
 * A hint that fits in a few words still belongs beside the control. `noStandingProse.test.ts` holds
 * that line, and this is where what does not fit goes.
 */
import { useId } from 'react';

import { useLocale } from '../i18n/LocaleProvider';

export interface InfoHintProps {
  /**
   * WHAT IT IS ABOUT, for somebody who cannot see the mark. The accessible name is
   * *"הסבר על …"* plus this, so a screen reader announces which explanation it is — a page with
   * four marks all announced as *"information"* is four identical controls.
   */
  readonly aboutHe: string;
  /** The explanation itself. Rendered only when the reader opens it. */
  readonly children: React.ReactNode;
}

export function InfoHint({ aboutHe, children }: InfoHintProps): JSX.Element {
  const { t } = useLocale();
  const id = useId();

  return (
    <details className="info-hint">
      <summary
        className="info-hint__mark"
        id={id}
        aria-label={`${t('common.explanationAbout')}${aboutHe}`}
        title={`${t('common.explanationAbout')}${aboutHe}`}
      >
        {/*
          THE LETTER, NOT AN IMAGE. It is one character in the page's own font, so it inherits the
          size and the colour of the text it sits beside and needs no asset, no sprite and no
          separate dark-mode variant.
        */}
        <span aria-hidden="true">i</span>
      </summary>
      <div className="info-hint__body">{children}</div>
    </details>
  );
}
